import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, MessageSquare } from "lucide-react";
import Layout, { PRIMARY_CTA, BookingLink } from "../components/Layout";
import SEO from "../components/SEO";
import { api, errorText } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOOLKIT_VISUAL = `${BACKEND}/api/pathway/Fleet_Clarity_Toolkit_Visual.png`;

const BANDS = {
  "Survival Pressure": "The business is under real operational pressure. Almost every category needs attention. Start with cash visibility and one weekly rhythm.",
  "Reactive Backend": "The company runs on reaction, not on rhythm. There are clear leaks — you just do not have a written map of them yet.",
  "Building Control": "You have installed real discipline. There are still gaps that only a professional diagnosis will fully surface.",
  "Scale-Ready Foundation": "You are already operating with unusual control. The next step is scaling without multiplying complexity.",
};

export default function FleetHealthResults() {
  const [params] = useSearchParams();
  const rid = params.get("rid");
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");

  // `api` is a stable module singleton; re-run only when `rid` changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!rid) { setErr("Missing result id"); return; }
    api.get(`/assessment/result/${rid}`).then(res => setR(res.data)).catch(e => setErr(errorText(e, "Result not found")));
  }, [rid]);

  if (err) return (
    <Layout minimal><section className="section"><div className="container-tight">
      <h1 className="font-bebas text-4xl uppercase mb-2" data-testid="result-error">Result not found</h1>
      <p className="text-[#A1A1AA]">{err}</p>
    </div></section></Layout>
  );

  if (!r) return <Layout minimal><section className="section"><div className="container-tight text-[#A1A1AA]">Loading…</div></section></Layout>;

  return (
    <Layout minimal>
      <SEO title="Your Fleet Crossroads Scorecard Result | Driver Reborn Advisory" description="Your personalized Fleet Crossroads Scorecard result and the next step Brandon recommends." noindex={true} />
      <section className="section">
        <div className="container-narrow">
          <div className="dr-progress-track mb-8"><div className="dr-progress-fill" style={{ width: "100%" }}/></div>

          <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">Your Fleet Crossroads Scorecard Result</div>
          <h1 className="font-bebas text-5xl sm:text-6xl uppercase leading-none mb-2" data-testid="result-band">
            {r.result_band}
          </h1>
          <div className="font-bebas text-3xl text-[#FF6A00] mb-6" data-testid="result-total">
            Score: {r.total_score} / 75
          </div>
          <p className="text-[#E6E6E6] leading-relaxed max-w-2xl mb-10" data-testid="result-band-desc">
            {BANDS[r.result_band]}
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div className="border border-[#2B2B2B] bg-[#111111] p-6">
              <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-3">Strongest area</div>
              <div className="font-bebas text-2xl uppercase" data-testid="result-strongest">{r.strongest_area}</div>
            </div>
            <div className="border border-[#2B2B2B] bg-[#111111] p-6">
              <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-3">Areas to strengthen first</div>
              <ul className="space-y-1" data-testid="result-weakest">
                {r.weakest_areas.map((w) => <li key={w} className="font-bebas text-xl uppercase">■ {w}</li>)}
              </ul>
            </div>
          </div>

          <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-3">Category scores</div>
          <div className="space-y-3 mb-10">
            {Object.entries(r.category_scores).map(([cat, val]) => (
              <div key={cat} data-testid={`result-cat-${cat.replace(/\s+/g,'-')}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{cat}</span>
                  <span className="text-[#FF6A00] font-bebas">{val}/15</span>
                </div>
                <div className="dr-progress-track">
                  <div className="dr-progress-fill" style={{ width: `${(val/15)*100}%` }}/>
                </div>
              </div>
            ))}
          </div>

          {r.ai_summary && (
            <div className="border border-[#FF6A00] bg-[#0A0A0A] p-6 mb-6" data-testid="ai-summary-block">
              <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">Brandon's personalized read</div>
              <h2 className="font-bebas text-3xl uppercase leading-tight mb-3" data-testid="ai-summary-headline">
                {r.ai_summary.headline}
              </h2>
              <p className="text-[#E6E6E6] leading-relaxed mb-5" data-testid="ai-summary-body">
                {r.ai_summary.summary}
              </p>
              {Array.isArray(r.ai_summary.priority_concerns) && r.ai_summary.priority_concerns.length > 0 && (
                <div className="mb-5">
                  <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">Priority concerns</div>
                  <ul className="space-y-2" data-testid="ai-summary-concerns">
                    {r.ai_summary.priority_concerns.map((c, i) => (
                      <li key={`${c.area || "concern"}-${i}`} className="flex items-start gap-3 text-[#E6E6E6]">
                        <span className="text-[#FF6A00] font-bebas mt-0.5 flex-shrink-0">■</span>
                        <span><b className="text-[#E6E6E6]">{c.area}</b> — {c.concern}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {r.ai_summary.first_move && (
                <div className="border-l-4 border-[#FF6A00] pl-4 mb-5" data-testid="ai-summary-first-move">
                  <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-1">First move this week</div>
                  <p className="text-[#E6E6E6] leading-relaxed">{r.ai_summary.first_move}</p>
                </div>
              )}
              <p className="text-xs text-[#A1A1AA] italic">
                AI-assisted, generated from your Scorecard answers. Educational — not financial, tax, or legal advice.
              </p>
            </div>
          )}

          <div className="border border-[#FF6A00] bg-[#111111] p-6 mb-6">
            <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">One dominant next step</div>
            <h2 className="font-bebas text-3xl uppercase mb-2">{PRIMARY_CTA}.</h2>
            <p className="text-sm text-[#A1A1AA] mb-4">
              Walk through your Scorecard result with Brandon — cost per truck, cash-flow visibility, owner dependence, and back-office discipline — and decide the correct next step.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <BookingLink testid="result-cta-conversation">
                <MessageSquare size={20}/> {PRIMARY_CTA} <ArrowRight size={16}/>
              </BookingLink>
              <Link to={`/apply?rid=${rid}`} className="btn-ghost" data-testid="result-cta-application">
                Prefer to send a written request?
              </Link>
            </div>
          </div>

          <div className="border border-[#2B2B2B] bg-[#0A0A0A] p-6 mb-6" data-testid="result-toolkit-bonus">
            <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-3">Completion bonus</div>
            <h3 className="font-bebas text-2xl uppercase mb-2">Driver Reborn Fleet Clarity Kit — How to Use It</h3>
            <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">
              A companion reference we deliver alongside your Scorecard result — a one-page walk-through of the Fleet Crossroads Scorecard, the Busy But Broke Profit Leak Map, the 7-Day Fleet Owner Clarity Sprint, and the Fleet Backend Readiness Checklist.
            </p>
            <img src={TOOLKIT_VISUAL} alt="Driver Reborn Fleet Clarity Kit — Welcome / How to Use This Kit" className="w-full h-auto border border-[#2B2B2B]" loading="lazy"/>
          </div>

          <p className="text-xs text-[#A1A1AA]">
            This is an educational screening result — not a financial, legal, tax, compliance, or guaranteed-profit diagnosis.
          </p>
        </div>
      </section>
    </Layout>
  );
}
